import { LegalPageLayout } from '../../components/layout/LegalPageLayout';
import { Helmet } from 'react-helmet-async';

export const PrivacyPage: React.FC = () => (
  <LegalPageLayout title="Privacy Policy" useH1={true}>
    <Helmet prioritizeSeoTags>
      <title>Privacy Policy | Cognitive Bias Lab</title>
      <meta name="description" content="Our privacy policy details how we handle your data when using the Cognitive Bias Lab platform. Learn about our data practices and your privacy rights." />
      <meta name="robots" content="noindex" />
      <link rel="canonical" href="https://www.cognitivebiaslab.com/privacy/" key="canonical" data-source="page-component" />
      
      {/* OpenGraph tags for better social sharing */}
      <meta property="og:title" content="Privacy Policy | Cognitive Bias Lab" />
      <meta property="og:description" content="Our privacy policy details how we handle your data when using the Cognitive Bias Lab platform. Learn about our data practices and your privacy rights." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.cognitivebiaslab.com/privacy/" />
      <meta property="og:site_name" content="Cognitive Bias Lab" />
      <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:type" content="image/webp" />
    </Helmet>
    <div className="space-y-8 max-w-4xl mx-auto">
      <div className="text-sm text-gray-500 italic">
        Last Updated: 31 March 2025
      </div>

      <p className="text-gray-600 leading-relaxed">
        Welcome to Cognitive Bias Lab. This Privacy Policy explains how we collect, use, and safeguard your 
        information when you visit our website, which is provided free of charge as an educational resource 
        on cognitive biases. By using our website, you agree to the practices described in this policy.
      </p>

      <div className="space-y-8">
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">1. Information We Collect</h2>
          <p className="text-gray-600 leading-relaxed">
            With your consent, we use analytics tools to collect anonymous usage data that helps us improve our website.
            Our analytics are configured with privacy in mind - your IP address is anonymized and we do not collect or store
            personally identifiable information.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">2. Our Consent Management System</h2>
          <p className="text-gray-600 leading-relaxed">
            We value your privacy and implement a comprehensive consent management system that gives you full control 
            over what data is collected during your visit. When you first access our site, you'll be presented with 
            a consent banner with clear options to either accept or reject non-essential cookies.
          </p>
          <p className="text-gray-600 leading-relaxed">
            <strong>Your choices:</strong>
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li><strong>Accept All:</strong> Allows analytics cookies that help us improve the website</li>
            <li><strong>Reject All:</strong> Only essential cookies for site functionality will be used</li>
            <li><strong>Manage Preferences:</strong> Access detailed cookie information and manage your preferences</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            Your consent preferences are stored locally and remembered across visits. You can change your preferences 
            at any time by clicking the "Manage Cookies" link in the footer of our website.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">3. Cookies We Use</h2>
          <p className="text-gray-600 leading-relaxed">
            Our website uses the following types of cookies:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li><strong>Essential cookies:</strong> Required for basic site functionality. These cannot be disabled.</li>
            <li><strong>Analytics cookies:</strong> Help us understand visitor behavior and improve our content. 
            These are only set with your explicit consent.</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            Google Analytics uses the following cookies when consent is granted:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li><strong>_ga:</strong> Used to distinguish users. Expires after 2 years.</li>
            <li><strong>_ga_[measurement ID]:</strong> Used to maintain session state. Expires after 2 years.</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            No cookies from third-party advertisers or social media platforms are used on our website.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">4. How We Use Your Information</h2>
          <p className="text-gray-600 leading-relaxed">
            The anonymous data we collect is used exclusively to:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li>Monitor and analyze website performance</li>
            <li>Improve our educational content and user experience</li>
            <li>Ensure that the website operates efficiently</li>
            <li>Understand content preferences to enhance future materials</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            We do not sell, rent, or otherwise disclose your data to external parties. Analytics data 
            is processed according to Google's Data Processing Terms and Google's Privacy Policy. We retain 
            analytics data for a maximum period of 14 months, after which it is automatically deleted.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">5. Data Security</h2>
          <p className="text-gray-600 leading-relaxed">
            We employ reasonable security measures to protect the limited data collected through our website. However, 
            no method of electronic transmission or storage is completely secure. While we strive to safeguard 
            your data, we cannot guarantee its absolute security.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">6. External Links</h2>
          <p className="text-gray-600 leading-relaxed">
            Our website may include links to external sites for your convenience. Please note that we do not 
            control the privacy practices of these external websites. We encourage you to review their privacy 
            policies when you visit them.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">7. Children's Privacy</h2>
          <p className="text-gray-600 leading-relaxed">
            Cognitive Bias Lab is not intended for children under the age of 13. We do not knowingly collect 
            data from children. If it comes to our attention that a child under 13 has provided us with personal 
            information, we will take steps to delete that information promptly.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">8. Your Privacy Rights</h2>
          <p className="text-gray-600 leading-relaxed">
            Depending on your location, you may have certain rights regarding your personal data under regulations like the GDPR or CCPA. These may include:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li>The right to access information we hold about you</li>
            <li>The right to request deletion of any personal data we may hold</li>
            <li>The right to object to processing of your data</li>
            <li>The right to withdraw consent at any time</li>
          </ul>
          <p className="text-gray-600 leading-relaxed">
            You can exercise these rights by:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 leading-relaxed">
            <li>Managing your cookie preferences through our consent banner or footer link</li>
            <li>Using browser controls to clear cookies and local storage</li>
            <li>Contacting us directly with specific requests</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">9. Changes to This Privacy Policy</h2>
          <p className="text-gray-600 leading-relaxed">
            We may update this Privacy Policy from time to time. Any changes will be posted on this page with 
            a revised "Last Updated" date. We encourage you to review this policy periodically to stay informed 
            about how we are protecting your information.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-800">10. Contact Us</h2>
          <p className="text-gray-600 leading-relaxed">
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at <a href="mailto:contact@cognitivebiaslab.com">contact@cognitivebiaslab.com</a>.
          </p>
        </section>
      </div>
    </div>
  </LegalPageLayout>
); 